import apisauce from 'apisauce';

import AuthService from '../services/authService';

const api = apisauce.create({
  baseURL: process.env.SITE_URL,
  timeout: 10000,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

if (AuthService.isLoggedIn()) {
  api.setHeader('authorization', `Bearer ${AuthService.getToken()}`);
}

export default {
  user: {
    signInAsAdmin: (password, email) => api
      .post('api/users/signin-admin', { password, email })
      .then((res) => {
        if (res?.data?.jwtToken) {
          api.setHeader('authorization', `Bearer ${res.data.jwtToken}`);
        }
        return res;
      }),
    signInAsManager: (email, password) => api
      .post('api/users/signin-manager', { email, password })
      .then((res) => {
        if (res?.data?.jwtToken) {
          api.setHeader('authorization', `Bearer ${res.data.jwtToken}`);
        }
        return res;
      }),
    signIn: (phone, password) => api
      .post('api/users/signin', { phone, password })
      .then((res) => {
        if (res?.data?.jwtToken) {
          api.setHeader('authorization', `Bearer ${res.data.jwtToken}`);
        }
        return res;
      }),
    signOut: () => api.setHeader('authorization', null),
    profile: {
      get: (userId) => api.get(`api/users/profile/${userId}`),
      getPackage: (phone) => api.get('api/users/package', { phone }),
      update: (userId, profile) => api.put(`api/users/profile/${userId}`, { profile }),
      setNewPhoneNumber: (userId, phone) => api.put(`api/users/profile/${userId}/phone`, { phone }),
      changeLanguage: (userId, lngCode) => api.put(`api/users/profile/${userId}/lang`, { lngCode }),
      resetPassword: (phone, password, token) => api
        .put('api/users/profile/password/reset', { phone, password, token }),
    },
    tfa: {
      resetPasswordRequest: (phone) => api.post('api/tfa/reset-password-request', { phone }),
      sendConfirmationCode: (userId, phone, isRegistration) => api.post('api/tfa/send-confirmation-code', { userId, phone, isRegistration }),
      sendConfirmationCodeViaViber: (userId, phone, isRegistration) => api.post('api/tfa/send-confirmation-code-via-viber', { userId, phone, isRegistration }),
      verifyConfirmationCode: (userId, token) => api.post('api/tfa/verify-confirmation-code', { userId, token }),
    },
    signUpBy: (phone) => api.post(`api/users/signup-by/${phone}`, {}),
    setPassword: (userId, firstName, lastName, password, region, delivery) => api
      .put('api/users/set-password', {
        userId,
        firstName,
        lastName,
        password,
        region,
        delivery,
      })
      .then((res) => {
        if (res?.data?.jwtToken) api.setHeader('authorization', `Bearer ${res.data.jwtToken}`);
        return res;
      }),
    signupByDealer: (firstName, lastName, phone, region, delivery, packageName, packageNameTranslation) => api
      .post('api/users/signup-by-dealer', {
        firstName,
        lastName,
        phone,
        region,
        delivery,
        packageName,
        packageNameTranslation,
      }, () => { throw new Error(); }),
    dealer: {
      updateUserPackageName: (userId, newPackage) => api.patch(`api/users/${userId}/package-name`,
        { newPackage },
        () => {
          throw new Error();
        }),
    },
    getClients: (queryParams = {}) => api.get('api/users/', queryParams, () => { throw new Error(); }),
  },
  config: {
    get: (systemType) => api.get('api/config', { systemType }),
    connectingProfiles: {
      list: (systemType) => api.get('api/config/connecting-profiles/list', { systemType }),
    },
    sideProfiles: {
      list: (systemType) => api.get('api/config/side-profiles/list', { systemType }),
    },
    frameProfiles: {
      list: () => api.get('api/config/frame-profiles/list'),
    },
    allSideProfiles: {
      list: () => api.get('api/config/all-side-profiles/list'),
    },
    getAluminiumColorsList: {
      list: () => api.get('api/config/aluminium-colors/list'),
    },
    createAluminiumColor: (color) => api.post('api/config/aluminium-color/create', { color }),
    editAluminiumColor: (articleCode, color) => api.patch('api/config/aluminium-color/edit', { articleCode, color }),
  },
  admin: {
    get: (startDate, endDate) => {
      api.setHeader('authorization', `Bearer ${AuthService.getToken()}`);
      return api.get('api/users/admin/get-admin-page-info', { startDate, endDate });
    },
  },
  json: {
    apply: {
      itemsListAndPricesJSON: (fileContent) => {
        api.setHeader('authorization', `Bearer ${AuthService.getToken()}`);
        return api.post('api/json/apply-data-from-items-list-json', { fileContent });
      },
      itemsListAndPricesZIP: (zip) => {
        api.setHeader('authorization', `Bearer ${AuthService.getToken()}`);
        return api.post('api/json/apply-data-from-items-list-zip', { zip });
      },
      customersJSON: (fileContent) => {
        api.setHeader('authorization', `Bearer ${AuthService.getToken()}`);
        return api.put('api/json/apply-data-from-customers-json', { fileContent });
      },
      pricelistByRegion: ({ fileContent, region }) => {
        api.setHeader('authorization', `Bearer ${AuthService.getToken()}`);
        return api.put('api/json/apply-pricelist-by-region', { fileContent, region });
      },
      discountsJSON: (fileContent) => {
        api.setHeader('authorization', `Bearer ${AuthService.getToken()}`);
        return api.put('api/json/apply-data-from-discounts-json', { fileContent });
      },
    },
  },
  priceList: {
    get: (region, packageName) => api.get('api/price-list', { region, packageName }),
  },
  delivery: {
    get: (region) => api.get('api/delivery', { region }),
  },
  discounts: {
    get: () => api.get('api/discounts'),
  },
  orders: {
    create: (userId, data, region, doorsSnippet, systemType, manageeInfo) => api
      .post('api/orders', {
        userId,
        data,
        region,
        doorsSnippet,
        systemType,
        manageeInfo,
      })
      .then((res) => res.data),
    copy: (userId, orderId, title) => api
      .post(`api/orders/${orderId}/copy`, { userId, orderId, title })
      .then((res) => res.data),
    update: (orderId, userId, data, region, doorsSnippet, systemType) => api
      .put(`api/orders/${orderId}/update`, { userId, data, region, doorsSnippet, systemType })
      .then((res) => res.data),
    putIntoWork: (orderId, userId, data) => api
      .put(`api/orders/${orderId}/put-into-work`, { userId, data })
      .then((res) => res.data),
    updateTitle: (orderId, userId, title) => api
      .put(`api/orders/${orderId}/update/title`, { userId, title })
      .then((res) => res.data),
    updatePriceByDealer: (orderId, price) => api
      .patch(`api/orders/${orderId}/update/price`, { price })
      .then((res) => res.data),
    delete: (orderId) => api
      .delete(`api/orders/${orderId}/delete`, { orderId })
      .then((res) => res.data),
    list: {
      acceptedByUserId: (userId, queryParams = {}) => api
        .get(`api/orders/${userId}/list/accepted`, queryParams),
      savedByUserId: (userId, queryParams = {}) => api
        .get(`api/orders/${userId}/list/saved`, queryParams),
    },
    get: (orderId) => api.get(`api/orders/${orderId}`),
    getOrderModalData: (orderId) => api.get(`api/orders/${orderId}/put-into-work-lead-up`),
  },
  ordersDraft: {
    create: (userId, data, doorsSnippet, systemType) => api
      .post('api/orders-draft', {
        userId,
        data,
        doorsSnippet,
        systemType,
      })
      .then((res) => res.data),
    update: (orderId, userId, data) => api
      .put(`api/orders-draft/${orderId}/update`, { userId, data })
      .then((res) => res.data),
    delete: (orderId) => api
      .delete(`api/orders-draft/${orderId}/delete`, { orderId })
      .then((res) => res.data),
    get: (orderId) => api.get(`api/orders-draft/${orderId}`),
  },
};
